import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Filters } from "@/components/users/filters.tsx";
import { UsersActiveReviewStrip } from "@/components/users/users-active-review-strip";
import {
  UsersTable,
  type UsersSort,
  type UsersSortDirection,
} from "@/components/users/users-table";
import { useUsers } from "@/lib/api/user.ts";
import { requirePrivilegedAccess } from "@/lib/casl/route-guards";
import { cn } from "@/lib/utils";
import { createFileRoute, useRouter, useSearch } from "@tanstack/react-router";

const PAGE_SIZES = [10, 20, 30, 50, 100, 500, 1000, 2000];

export const Route = createFileRoute("/_authenticated/users/")({
  component: Users,
  beforeLoad: ({ context }) => {
    requirePrivilegedAccess(context.user);
  },
});

function Users() {
  const router = useRouter();
  const {
    page,
    search,
    assignedRole,
    per_page,
    group,
    status,
    sort,
    direction,
  } = useSearch({
    strict: false,
  }) as Record<string, string>;

  const currentPage = parseInt(page || "1", 10);
  const pageSize = parseInt(per_page || "50", 10);
  const statusFilter = status || "active";
  const sortColumn: UsersSort | null =
    sort === "name" || sort === "joined" ? sort : null;
  const sortDirection: UsersSortDirection | null = sortColumn
    ? direction === "asc"
      ? "asc"
      : "desc"
    : null;

  const { data, isPending, isFetching, isPlaceholderData } = useUsers({
    page: currentPage,
    perPage: pageSize,
    search,
    assignedRole,
    group,
    status: statusFilter,
    matchedMentees: true,
    sort: sortColumn ?? undefined,
    direction: sortDirection ?? undefined,
  });

  const setSort = (next: UsersSort) => {
    const clearing = sortColumn === next && sortDirection === "desc";
    const nextDirection: UsersSortDirection =
      sortColumn === next && sortDirection === "asc" ? "desc" : "asc";
    router.navigate({
      to: router.state.location.pathname,
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        sort: clearing ? undefined : next,
        direction: clearing ? undefined : nextDirection,
        page: 1,
      }),
    });
  };

  const goToPage = (nextPage: number, nextPageSize = pageSize) => {
    router.navigate({
      to: router.state.location.pathname,
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        page: nextPage,
        per_page: nextPageSize,
      }),
    });
  };

  return (
    <div className="m-xl flex flex-col gap-lg pb-3xl">
      <h1 className="font-heading text-header-3">Users</h1>
      <UsersActiveReviewStrip />
      <Filters />
      <UsersTable
        users={data?.users ?? []}
        isPending={isPending}
        isRefreshing={isFetching && !isPending}
        sort={sortColumn}
        direction={sortDirection}
        onSort={setSort}
      />
      <div className="flex items-center justify-end gap-xs font-body text-body-2 text-text-secondary">
        <span className="px-sm">Rows</span>
        <Select
          value={String(pageSize)}
          onValueChange={(value) => goToPage(1, Number(value))}
        >
          <SelectTrigger
            className="h-10 w-[84px] rounded-xl border-buttonBorder-tertiary bg-surfaces-z0 px-md font-body text-body-2 shadow-none"
            aria-label="Rows per page"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-2xl border-buttonBorder-tertiary font-body">
            {PAGE_SIZES.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <PagerButton
          disabled={currentPage <= 1}
          onClick={() => goToPage(currentPage - 1)}
        >
          Previous
        </PagerButton>
        <span className="px-md text-text-primary">Page {currentPage}</span>
        <PagerButton
          disabled={isPlaceholderData || data?.isLastPage !== false}
          onClick={() => goToPage(currentPage + 1)}
        >
          Next
        </PagerButton>
      </div>
    </div>
  );
}

function PagerButton({
  disabled,
  onClick,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "h-10 rounded-xl px-md font-medium",
        disabled
          ? "text-text-placeholder"
          : "text-text-primary hover:bg-surfaces-z3",
      )}
    >
      {children}
    </button>
  );
}
